<script lang="ts">
  import { app, persistSettings } from './store.svelte';
  import { t } from './i18n';
  import { parseStl, centerOnPlate } from '../core/mesh';
  import { onMount } from 'svelte';
  import { DEMO_GALLERY, loadDemo, type DemoItem } from './demoGallery';

  interface Props {
    onClose: () => void;
  }
  const { onClose }: Props = $props();

  let fileInput: HTMLInputElement | undefined = $state();
  let gcodeInput: HTMLInputElement | undefined = $state();
  let modalEl: HTMLDivElement | undefined = $state();
  let prevFocus: HTMLElement | null = null;
  let showGallery = $state(false);
  let galleryError = $state('');

  async function loadGalleryItem(item: DemoItem) {
    galleryError = '';
    try {
      app.status = 'loading';
      const tris = await loadDemo(item);
      app.triangles = centerOnPlate(tris);
      app.fileName = item.name;
      app.sliceResult = null;
      app.gcodeOutput = null;
      app.viewMode = 'mesh';
      app.mode = 'edit';
      app.showVasePanel = false; // gallery items are non-vase meshes
      app.status = 'ready';
      onClose();
    } catch (err) {
      galleryError = (err as Error).message;
      app.status = 'idle';
    }
  }

  onMount(() => {
    prevFocus = document.activeElement as HTMLElement | null;
    queueMicrotask(() => modalEl?.querySelector<HTMLElement>('button:not([disabled])')?.focus());
    return () => prevFocus?.focus?.();
  });

  function trapTab(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !modalEl) return;
    const focusables = Array.from(modalEl.querySelectorAll<HTMLElement>(
      'button:not([disabled]),[href],input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
    )).filter((n) => n.offsetParent !== null);
    if (focusables.length === 0) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement;
    if (e.shiftKey && active === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && active === last) { first.focus(); e.preventDefault(); }
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    trapTab(e);
  }

  async function handleStl(file: File) {
    if (!file) return;
    app.status = 'loading';
    app.errorMessage = '';
    app.fileName = file.name;
    try {
      const buf = await file.arrayBuffer();
      const tris = parseStl(buf);
      if (tris.length === 0) {
        app.errorMessage = 'Empty STL';
        app.status = 'idle';
        return;
      }
      app.triangles = centerOnPlate(tris);
      app.sliceResult = null;
      app.gcodeOutput = null;
      app.viewMode = 'mesh';
      app.status = 'ready';
      app.mode = 'edit';
      app.showVasePanel = false; // imported STL is a non-vase mesh
      onClose();
    } catch (err) {
      app.errorMessage = (err as Error).message;
      app.status = 'idle';
    }
  }

  async function handleGcode(file: File) {
    if (!file) return;
    const text = await file.text();
    // Display imported G-code in the right panel as preview-only.
    app.gcodeOutput = {
      gcode: text,
      stats: {
        layerCount: (text.match(/^G[01].*?Z[\d.]+/gm) ?? []).length,
        estimatedTimeMin: 0,
        estimatedClayMm3: 0,
        bbox: { min: [0, 0, 0], max: [0, 0, 0] }
      }
    };
    app.fileName = file.name;
    app.rightPanel = 'gcode';
    onClose();
  }

  function openVase() {
    app.showVasePanel = true;
    onClose();
  }
</script>

<div class="overlay" onclick={onClose} onkeydown={onKey} role="presentation">
  <div bind:this={modalEl} class="modal" onclick={(e) => e.stopPropagation()} onkeydown={onKey}
    role="dialog" aria-modal="true" aria-labelledby="insert-title" tabindex="-1">
    <h2 id="insert-title">{t(app.lang, 'panel.insert')}</h2>
    <div class="grid">
      <input type="file" accept=".stl" bind:this={fileInput}
        onchange={(e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) handleStl(f); }}
        style="display:none" />
      <button class="card" onclick={() => fileInput?.click()}>
        <div class="icon mesh">▣</div>
        <div class="card-title">{t(app.lang, 'insert.mesh')}</div>
        <div class="card-ext">{t(app.lang, 'insert.meshExt')}</div>
      </button>

      <button class="card" onclick={openVase}>
        <div class="icon vase">⛁</div>
        <div class="card-title">{t(app.lang, 'insert.vase')}</div>
        <div class="card-ext">{t(app.lang, 'insert.vaseExt')}</div>
      </button>

      <input type="file" accept=".gcode,.gco,.g" bind:this={gcodeInput}
        onchange={(e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) handleGcode(f); }}
        style="display:none" />
      <button class="card" onclick={() => gcodeInput?.click()}>
        <div class="icon gcode">⌘</div>
        <div class="card-title">{t(app.lang, 'insert.gcode')}</div>
        <div class="card-ext">{t(app.lang, 'insert.gcodeExt')}</div>
      </button>

      <button class="card disabled" disabled title="Yakında">
        <div class="icon spline">∿</div>
        <div class="card-title">{t(app.lang, 'insert.spline')}</div>
        <div class="card-ext">{t(app.lang, 'insert.splineExt')}</div>
      </button>
    </div>

    <button class="gallery-toggle" onclick={() => (showGallery = !showGallery)}>
      <span class="ms small">{showGallery ? 'expand_less' : 'expand_more'}</span>
      Galeri ({DEMO_GALLERY.length} örnek)
    </button>
    {#if showGallery}
      <div class="gallery">
        {#each DEMO_GALLERY as item (item.id)}
          <button class="gallery-item" onclick={() => loadGalleryItem(item)} title={item.description}>
            <span class="ms">view_in_ar</span>
            <div class="gi-text">
              <span class="gi-name">{item.name}</span>
              <span class="gi-desc">{item.description}</span>
            </div>
          </button>
        {/each}
        {#if galleryError}
          <div class="gallery-error">{galleryError}</div>
        {/if}
      </div>
    {/if}

    <button class="cancel" onclick={onClose}>{t(app.lang, 'btn.cancel')}</button>
  </div>
</div>

<style>
  .overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.6);
    display: grid; place-items: center;
    z-index: 100;
  }
  .modal {
    background: var(--bg-1);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 24px;
    min-width: 480px;
  }
  h2 { margin: 0 0 16px; font-size: 16px; color: var(--accent); text-transform: uppercase; letter-spacing: 0.05em; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .card {
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    text-align: left;
    color: var(--fg-1);
    transition: border-color 0.15s, background 0.15s;
    display: flex; flex-direction: column; gap: 6px;
  }
  .card:hover:not(.disabled) {
    border-color: var(--accent);
    background: rgba(200, 123, 58, 0.08);
  }
  .card.disabled { opacity: 0.4; cursor: not-allowed; }
  .icon {
    width: 36px; height: 36px;
    border-radius: 8px;
    display: grid; place-items: center;
    color: white; font-size: 22px;
    background: var(--accent);
  }
  .icon.vase { background: #d6884a; }
  .icon.gcode { background: #5a8ec8; }
  .icon.spline { background: #888; }
  .card-title { font-weight: 500; font-size: 14px; }
  .card-ext { font-size: 11px; color: var(--fg-2); }
  .cancel {
    margin-top: 16px;
    width: 100%;
    background: transparent;
    color: var(--fg-2);
    border: 1px solid var(--border);
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
  }
  .gallery-toggle {
    margin-top: 14px; width: 100%;
    background: transparent; border: 1px dashed var(--border);
    color: var(--fg-2); padding: 8px; border-radius: 6px; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    font-size: 12px; font-family: inherit;
  }
  .gallery-toggle:hover { color: var(--fg-1); border-color: var(--accent); }
  .gallery {
    margin-top: 8px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
    max-height: 240px; overflow-y: auto;
  }
  .gallery-item {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px; background: var(--bg-2); color: var(--fg-1);
    border: 1px solid var(--border); border-radius: 6px;
    cursor: pointer; text-align: left; font-family: inherit;
  }
  .gallery-item:hover { border-color: var(--accent); background: var(--bg-3); }
  .gallery-item .ms { font-size: 18px; color: var(--accent); }
  .gi-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .gi-name { font-size: 12px; font-weight: 500; }
  .gi-desc { font-size: 10px; color: var(--fg-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .gallery-error { grid-column: 1 / -1; color: #ffaaa0; font-size: 11px; padding: 6px; }
</style>
